/**
 * L'EN-TÊTE DU COMPTEUR (DEV-1508 lots 2 et 5b) — les faits SERVIS, et le titre
 * qui porte l'état du point.
 * ============================================================================
 *
 * INTENTION : avant de regarder une courbe, on veut savoir de QUEL compteur on
 * parle, et dans quel état il est. Le titre le dit d'un coup d'œil (« Compteur
 * C5 (LINKY), à jour ») ; la grille dessous porte les faits contractuels et
 * techniques tels qu'Enedis les sert.
 *
 * CE QUE LE LOT 5b CORRIGE, relevé sur les trois compteurs témoins le 15/09 :
 *  • « Plages horaires » lisait `comptage.plage_releve` — le code de la plage
 *    MENSUELLE DE RELEVÉ (`TF15`, `TD00`), qui n'est pas une plage horaire et
 *    vaut `null` sur le C5. Les heures creuses servies (01:06 → 06:36 et
 *    14:36 → 17:06) n'étaient nulle part. Deux faits, deux lignes : les plages
 *    horaires viennent de `comptage.plages_horaires`, le code de relevé rejoint
 *    la modalité de relève ;
 *  • la puissance SOUSCRITE (6 kVA sur le C5) n'était pas servie du tout, et la
 *    page n'affichait que la puissance de RACCORDEMENT (9 kVA) : deux faits que
 *    la facture distingue, et qu'un client lit différemment ;
 *  • le consentement disait « Actif » sans sa date de fin (20/05/2031 servie).
 *
 * TROIS RÈGLES, qui tiennent tout :
 *  • un fait qu'on ne connaît pas vaut `null` et ne s'affiche pas. Jamais un 0,
 *    jamais un tiret qui ferait croire à une absence mesurée ;
 *  • la FRAÎCHEUR ne se calcule pas ici. Elle se lit du verdict ou du statut
 *    servi, sur l'attendu de CE compteur : aucun seuil de jours ne vit dans le
 *    front (E4). Le titre ne fait que RECOPIER ce qu'elle a déjà conclu ;
 *  • un état SERVI par Enedis (`etat` : résilié, inexistant) prime sur la
 *    fraîcheur de la collecte. Un compteur résilié n'est pas « en retard de
 *    72 j » : il est résilié, et la fraîcheur se tait (leçon DEV-1619 : jamais
 *    deux états contradictoires sur le même écran).
 */
import type { ConsentementEtat, InformationPDL } from "@types"
import type { DirectionEnedis } from "@modules/data/services/sourceDataEnedis.service"
import { LIBELLES_DIRECTION } from "@modules/data/utils/ongletsEnedis"
import {
    dateFr,
    estInterrompue,
    type Fraicheur,
} from "@modules/data/utils/fraicheurEnedis"

/**
 * Le mot du consentement. `unknown` n'a PAS de mot : un état non connu ne
 * s'écrit pas « Inconnu » comme s'il avait été constaté, il ne s'affiche pas.
 */
export const LIBELLES_CONSENTEMENT: Record<ConsentementEtat, string | null> = {
    active: "Actif",
    revoked: "Révoqué",
    unsigned: "Non signé",
    outdated: "Expiré",
    unknown: null,
}

export type FaitEntete = {
    cle: string
    libelle: string
    /** `null` = fait non connu. L'écran n'affiche rien, il n'invente pas. */
    valeur: string | null
    /**
     * Vrai quand la valeur est un IDENTIFIANT à lire chiffre par chiffre (le
     * numéro du point) : l'écran la rend en chasse fixe pour qu'on puisse la
     * comparer à celle d'une facture sans compter sur ses doigts.
     */
    mono?: boolean
}

function texte(valeur: unknown): string | null {
    if (valeur === null || valeur === undefined) return null
    const t = String(valeur).trim()
    return t === "" ? null : t
}

/** `12.5` → `12,5` n'est PAS fait ici : une puissance Enedis est un entier ou une décimale simple. */
function kva(valeur: unknown): string | null {
    return typeof valeur === "number" && Number.isFinite(valeur)
        ? `${valeur} kVA`
        : null
}

/**
 * La FORMULE TARIFAIRE, telle qu'elle se lit.
 *
 * ⚠️ `structure_tarifaire.formule_tarifaire_acheminement` n'est PAS une chaîne :
 * c'est un objet `{ date_derniere_modification, fta }` (`information_pdl.d.ts`).
 * Passé tel quel à un `String()`, il s'imprime `[object Object]` — c'est ce que
 * la page ombre a servi le 10/09 sur un C5 témoin. Le fait à montrer est le CODE
 * de la formule (`fta`), rien d'autre : la date de dernière modification est un
 * autre fait, et il n'est pas dans ceux-ci.
 *
 * La forme chaîne reste acceptée : d'anciennes charges utiles la portent, et une
 * chaîne servie est un fait lisible. Tout le reste vaut `null` : un objet dont on
 * ne sait pas lire la formule ne s'écrit pas, il s'omet.
 */
export function formuleTarifaire(
    informations: InformationPDL | null | undefined
): string | null {
    const formule = informations?.structure_tarifaire
        ?.formule_tarifaire_acheminement as unknown
    if (typeof formule === "string") return texte(formule)
    if (formule && typeof formule === "object") {
        return texte((formule as { fta?: unknown }).fta)
    }
    return null
}

/**
 * L'OPTION DU CALENDRIER FOURNISSEUR, sur SA propre ligne (S2 du 23/09).
 *
 * Elle était fondue dans la formule (« BTINFCU4 · Base ») là où la carte du
 * compteur en fait deux lignes titrées « FTA » et « Calendrier fournisseur ».
 * Deux faits, deux lignes, les mêmes mots des deux côtés.
 */
export function calendrierFournisseur(
    informations: InformationPDL | null | undefined
): string | null {
    return texte(
        informations?.structure_tarifaire?.calendrier_fournisseur?.libelle
    )
}

/** Le pas de la courbe, avec le sens auquel il s'applique. */
export function pasDeLaCourbe(
    informations: InformationPDL | null | undefined,
    direction: DirectionEnedis
): string | null {
    const cdc =
        direction === "soutirage"
            ? informations?.data?.soutirage?.cdc
            : informations?.data?.injection?.cdc
    return texte(cdc?.pas)
}

// ============================================================================
// LES PLAGES HORAIRES — le bon champ, celui qui porte des HEURES (écart E2)
// ============================================================================

/** `01:06:00` → `01:06`. Les secondes d'une plage horaire ne disent rien. */
function heureCourte(valeur: unknown): string | null {
    const t = texte(valeur)
    if (!t) return null
    return /^\d{2}:\d{2}/.test(t) ? t.slice(0, 5) : t
}

function plagesEnClair(
    plages: Array<{ time_from?: unknown; time_to?: unknown }> | null | undefined
): string | null {
    if (!Array.isArray(plages) || plages.length === 0) return null
    const ecrites = plages
        .map((p) => {
            const de = heureCourte(p?.time_from)
            const a = heureCourte(p?.time_to)
            return de && a ? `${de} → ${a}` : null
        })
        .filter((p): p is string => p !== null)
    return ecrites.length > 0 ? ecrites.join(" · ") : null
}

/**
 * Les plages horaires du compteur : heures creuses, et pointes fixes quand
 * elles existent.
 *
 * « HC 01:06 → 06:36 · 14:36 → 17:06 », « HC 22:00 → 06:00 · Pointes
 * 09:00 → 11:00 · 18:00 → 20:00 ». `plage_releve` n'entre JAMAIS ici : `TF15`
 * et `TD00` sont des codes de plage de RELEVÉ mensuel, pas des heures.
 */
export function plagesHoraires(
    informations: InformationPDL | null | undefined
): string | null {
    const plages = informations?.comptage?.plages_horaires
    const creuses = plagesEnClair(plages?.heures_creuses)
    const pointes = plagesEnClair(plages?.heures_pointe_fixe)
    const morceaux: string[] = []
    if (creuses) morceaux.push(`HC ${creuses}`)
    if (pointes) morceaux.push(`Pointes ${pointes}`)
    return morceaux.length > 0 ? morceaux.join(" · ") : null
}

// ============================================================================
// LA PUISSANCE SOUSCRITE — un nombre, ou un dénivelé par classe (écart E2)
// ============================================================================

/**
 * L'ordre des classes temporelles, RECOPIÉ DE LA CARTE (S2 du 23/09) :
 * `MeterPdlDeniveleViewer.vue` peint la saison haute, puis la saison basse,
 * puis la Pointe fixe. La page rangeait la Pointe en tête ; deux écrans du même
 * client donnaient le même dénivelé dans deux ordres différents.
 */
const ORDRE_DENIVELE = ["HPH", "HCH", "HPE", "HCE", "PTE"] as const

/**
 * LA PUISSANCE SOUSCRITE QUAND ELLE EST UN SEUL NOMBRE (`Puiss. souscrite` sur
 * la carte). Le dénivelé est un AUTRE fait, sur sa propre ligne : la carte les
 * sépare, et les fondre ici faisait porter deux libellés à une seule ligne.
 */
export function puissanceSouscrite(
    informations: InformationPDL | null | undefined
): string | null {
    return kva(
        informations?.structure_tarifaire?.puissance_souscrite_soutirage
            ?.puissance
    )
}

/**
 * L'UNITÉ DU DÉNIVELÉ, TELLE QUE LA CARTE LA CHOISIT.
 *
 * ⚠️ RÈGLE LUE, PAS ÉCRITE ICI : `MeterPdlDeniveleViewer.vue:85` rend
 * `denivele.PTE != null ? "kVA" : "kW"`. La page écrivait « kVA » partout, et
 * sur le C4 témoin (`638f1080`, PTE non servie) la carte affichait 66 kW quand
 * la page affichait 66 kVA. Deux unités pour le même nombre : c'est l'écart le
 * plus net relevé par Mathieu le 23/09, et c'est la carte qui fait foi.
 *
 * Aucune conversion n'est faite : seule l'ÉTIQUETTE change, comme sur la carte.
 * Laquelle des deux unités dapi sert réellement n'est pas tranchée ici.
 */
function uniteDuDenivele(denivele: Record<string, unknown>): string {
    return denivele.PTE != null ? "kVA" : "kW"
}

/**
 * LE DÉNIVELÉ DE PUISSANCE, une ligne à lui (« Dénivelé de puissance » sur la
 * carte). Les classes ne sont nommées que lorsque les puissances DIFFÈRENT :
 * sur les deux témoins C4 et C2 elles sont toutes égales, et « 800 kVA » se lit
 * mieux que cinq fois 800.
 */
export function deniveleSouscrit(
    informations: InformationPDL | null | undefined
): string | null {
    const denivele =
        informations?.structure_tarifaire?.puissance_souscrite_soutirage
            ?.denivele
    if (!denivele) return null
    const unite = uniteDuDenivele(denivele as Record<string, unknown>)
    const servies = ORDRE_DENIVELE.map((classe) => ({
        classe,
        valeur: denivele[classe],
    })).filter(
        (c): c is { classe: (typeof ORDRE_DENIVELE)[number]; valeur: number } =>
            typeof c.valeur === "number" && Number.isFinite(c.valeur)
    )
    if (servies.length === 0) return null
    const toutesEgales = servies.every((c) => c.valeur === servies[0].valeur)
    if (toutesEgales) return `${servies[0].valeur} ${unite}`
    return servies.map((c) => `${c.classe} ${c.valeur} ${unite}`).join(" · ")
}

// ============================================================================
// LA RELÈVE, LE CONSENTEMENT DATÉ, LA MISE EN SERVICE, LA PRODUCTION (Q12)
// ============================================================================

/**
 * Les modalités de relève : le FAIT DE RELÈVE tel que la carte l'écrit (mode et
 * média joints par Enedis, « Télérelevé IP »), et le code de la plage de relevé
 * mensuel (`TD00`, `TF15`) quand il l'est. Le code n'est pas de notre
 * plomberie : il figure sur le contrat d'acheminement du client.
 *
 * LE MÉDIA REJOINT LE MODE DEPUIS LE 23/09 (S2) : la carte écrivait « Télérelevé
 * IP » et la page « Télérelevé · TF15 ». Le média manquait d'un côté, le code de
 * plage de l'autre — et les deux écrans se lisaient comme deux faits différents
 * là où il n'y en a qu'un. Ici, les deux surfaces disent le même fait de relève,
 * et la page y ajoute le code que la carte ne porte pas.
 */
export function modaliteDeReleve(
    informations: InformationPDL | null | undefined
): string | null {
    const releve = informations?.compteur?.releve
    const fait =
        [texte(releve?.mode?.libelle), texte(releve?.media?.libelle)]
            .filter(Boolean)
            .join(" ") || null
    const plage = texte(informations?.comptage?.plage_releve)
    return [fait, plage].filter(Boolean).join(" · ") || null
}

/**
 * Le consentement, avec sa date de FIN quand elle est servie : « Actif →
 * 20/05/2031 ». Sans la date, l'état seul — un consentement dont on ignore le
 * terme n'est pas un consentement sans terme.
 */
export function consentementServi(
    informations: InformationPDL | null | undefined
): string | null {
    const etat = informations?.consentement?.etat
    const mot = etat ? (LIBELLES_CONSENTEMENT[etat] ?? null) : null
    if (!mot) return null
    const fin = dateFr(
        informations?.consentement?.date_to as string | null | undefined
    )
    return fin ? `${mot} → ${fin}` : mot
}

/**
 * La dernière mise en service, PAR SENS (fait servi depuis le lot 1b).
 *
 * ⚠️ À ne pas confondre avec `data.<sens>.date_from`, qui est la première
 * donnée DÉTENUE par nous : une mise en service de 2019 sur un compteur collecté
 * depuis 2024 est un fait tout différent, et l'un ne remplace pas l'autre.
 * Aucun des trois témoins ne servait ce bloc le 15/09 : la ligne est alors
 * simplement absente.
 */
export function miseEnService(
    informations: InformationPDL | null | undefined,
    directions: DirectionEnedis[]
): string | null {
    const bloc = informations?.mise_en_service
    if (!bloc) return null
    const dates = directions.map((sens) => ({
        sens,
        date: dateFr(bloc[sens] as string | null | undefined),
    }))
    const servies = dates.filter(
        (d): d is { sens: DirectionEnedis; date: string } => d.date !== null
    )
    if (servies.length === 0) return null
    if (servies.length === 1 && directions.length === 1) return servies[0].date
    return servies
        .map((d) => `${LIBELLES_DIRECTION[d.sens]} ${d.date}`)
        .join(" · ")
}

/**
 * L'installation de production déclarée pour le point (fait servi depuis le
 * lot 1b), avec sa puissance souscrite en injection quand le contrat la porte.
 *
 * ⚠️ TRI-ÉTAT tenu : `null` = le flux contractuel n'a pas été lu (« je ne sais
 * pas »), `[]` = lu, et rien de déclaré. Dans les DEUX cas la ligne est absente
 * — mais pour des raisons opposées, et aucune ne s'écrit « pas de production ».
 */
export function installationDeProduction(
    informations: InformationPDL | null | undefined
): string | null {
    const installations = informations?.installations_production
    const puissance = kva(
        informations?.injection?.structure_tarifaire
            ?.puissance_souscrite_injection?.puissance
    )
    if (!Array.isArray(installations) || installations.length === 0) {
        return null
    }
    const natures = installations
        .map((i) => texte(i?.filiere) ?? texte(i?.technologie))
        .filter((n): n is string => n !== null)
    if (natures.length === 0) return puissance
    return [natures.join(" · "), puissance].filter(Boolean).join(" · ")
}

/**
 * Les sens que ce point porte — écrits SEULEMENT quand ils disent quelque chose.
 *
 * Deux sens : la ligne rappelle qu'ils ne se somment JAMAIS (E5). Injection
 * seule : elle prévient qu'il n'y a pas de soutirage à chercher sur ce point.
 * Soutirage seul, le cas ordinaire : rien à dire, et une ligne « Directions :
 * Soutirage » sur les quatre cinquièmes du parc n'apprend rien à personne.
 */
export function directionsServies(
    directions: DirectionEnedis[]
): string | null {
    if (directions.length > 1) return "Soutirage et injection, jamais sommées"
    // UN PRODUCTEUR PUR LE DIT (lot 5g). « Injection seule » suffisait à écarter
    // le soutirage, mais pas à expliquer POURQUOI ce point n'en a pas : le mot
    // qui manquait est celui du métier du client sur ce compteur.
    if (directions[0] === "injection") return "Injection seule (producteur)"
    return null
}

// ============================================================================
// L'ÉTAT SERVI PAR ENEDIS — il prime sur la fraîcheur de la collecte
// ============================================================================

export type EtatServi = {
    cle: "resilie" | "inexistant"
    /** Ce que le TITRE en dit, sans virgule de tête. */
    suffixe: string
}

/**
 * L'état du point tel qu'Enedis le sert, quand ce n'est pas « en service ».
 *
 * Il prime sur tout : `PageSourceData` sert déjà une cause pour un compteur
 * inexistant, et un compteur résilié ne reçoit plus de mesures par construction.
 * Laisser la fraîcheur écrire « En retard de 72 j » à côté donnerait deux états
 * contradictoires sur le même écran (DEV-1619, §7.7 du plan de rattrapage).
 */
export function etatServiDuCompteur(
    informations: InformationPDL | null | undefined
): EtatServi | null {
    const etat = informations?.etat
    if (etat === "resilie") return { cle: "resilie", suffixe: "résilié" }
    if (etat === "inexistant") {
        return { cle: "inexistant", suffixe: "inexistant sur le réseau" }
    }
    return null
}

/**
 * LE TITRE DE LA CARTE (écart E1, question Q4 tranchée par le pilote le 15/09).
 *
 * « Compteur {segment} ({modèle}) » toujours ; le suffixe d'état SEULEMENT
 * quand un fait le porte. Le modèle est écrit tel qu'Enedis le sert (`LINKY`,
 * `SAPHIR`) : la grille dessous l'écrit déjà ainsi, et récrire un fait servi
 * pour le rendre joli est le premier pas vers un fait inventé.
 *
 * LE SUFFIXE, ET RIEN QU'À PARTIR DE CE QUI EST SERVI :
 *  • un état Enedis (résilié, inexistant) gagne toujours ;
 *  • à jour → « , à jour » ;
 *  • jamais aucune mesure → « , aucune mesure reçue » ;
 *  • un retard COMPTÉ → « , en retard de 74 j » ;
 *  • un retard non compté mais une dernière mesure connue → « , arrêté depuis
 *    le 28/06 ». On sait QUAND ça s'est arrêté, pas de combien : on écrit ce
 *    qu'on sait, pas un nombre de jours calculé ici (E4) ;
 *  • rien de tout cela → pas de suffixe. Un compteur dont la fraîcheur n'est
 *    pas servie n'est jamais dit « à jour ».
 */
export function titreDuCompteur(params: {
    informations: InformationPDL | null | undefined
    fraicheur?: Fraicheur | null
}): string {
    const compteur = params.informations?.compteur
    const segment = texte(compteur?.segment)
    const modele = texte(compteur?.modele)
    const base = segment
        ? modele
            ? `Compteur ${segment} (${modele})`
            : `Compteur ${segment}`
        : "Le compteur"
    return `${base}${suffixeDEtat(params.informations, params.fraicheur)}`
}

function suffixeDEtat(
    informations: InformationPDL | null | undefined,
    fraicheur: Fraicheur | null | undefined
): string {
    const servi = etatServiDuCompteur(informations)
    if (servi) return `, ${servi.suffixe}`
    if (!fraicheur) return ""
    // LE DROIT DE COLLECTE TOMBÉ PRIME SUR LE RESTE (lot 6a, volet 1) : « en
    // retard » dit au client qu'une mesure est en chemin. Sur un compteur dont
    // le consentement est révoqué, il n'en vient plus aucune, et le titre doit
    // dire l'état réel — le mot est celui de la carte du compteur (DEV-1620).
    // Élargi le 27/10 (décision de Mathieu) à toute collecte INTERROMPUE : le
    // compteur non téléopérable n'a perdu aucun droit, mais rien n'est en chemin
    // non plus, et « en retard » le dirait.
    if (estInterrompue(fraicheur)) return ", collecte interrompue"
    // LE VERDICT PAR FLUX PRIME (DEV-1845 tranche 2, Q4) : « à jour » seulement
    // si TOUS les flux dus le sont, « en retard » dès qu'un seul décroche. Et
    // RIEN quand le silence des uns empêche de conclure — un titre n'est pas
    // l'endroit où dire une moitié de verdict.
    if (fraicheur.tousAJour !== undefined) {
        if (fraicheur.tousAJour === true) return ", à jour"
        if (fraicheur.tousAJour === false) return ", en retard"
        return ""
    }
    // UN STATUT QUI SE CONTREDIT NE DONNE PAS DE SUFFIXE (complément du 17/09,
    // Q4 : jamais un état déduit). Ni « , à jour », qui serait faux, ni un
    // retard, qu'on ne connaît pas : le titre nomme le compteur, et la ligne
    // de fraîcheur dessous porte la date.
    if (fraicheur.verdictSu === false) return ""
    if (fraicheur.aJour) return ", à jour"
    if (fraicheur.aucuneMesure) return ", aucune mesure reçue"
    if (typeof fraicheur.retardJours === "number") {
        return `, en retard de ${fraicheur.retardJours} j`
    }
    const arret = dateFr(fraicheur.derniereMesure)
    if (arret) return `, arrêté depuis le ${arret.slice(0, 5)}`
    return ", en retard"
}

// ============================================================================
// LA GRILLE DES FAITS
// ============================================================================

/**
 * Les faits, dans l'ordre où on les lit : qui est ce point, comment il est
 * relevé et raccordé, ce que son contrat porte, et jusqu'à quand on a le droit
 * de le lire. `prm` est passé par l'écran : le numéro du point n'est pas dans
 * les informations servies.
 *
 * L'ORDRE ET LES LIBELLÉS SONT CEUX DE LA CARTE `/source/<id>/info-general`
 * (S2 de la review du 23/09 : « il faut que les informations soient
 * parfaitement cohérentes »). La carte fait foi, cette page s'aligne sur elle :
 * technique d'abord, contrat ensuite, `Calendrier fournisseur` puis `FTA` puis
 * `Puiss. souscrite` puis `Dénivelé de puissance`.
 *
 * ⚠️ LES LIBELLÉS SONT RECOPIÉS, PAS PARTAGÉS, et c'est un pis-aller assumé :
 * la carte les déclare dans un `useI18n` LOCAL à `MeterCardTechnicalData.vue`
 * (clés `pdl.*`), donc hors d'atteinte d'un import. Les centraliser demande de
 * toucher `core/components/source/meter/**`, hors périmètre de ce lot. Tant que
 * ce n'est pas fait, un libellé changé côté carte ne suit pas ici.
 *
 * TROIS FAITS DE LA CARTE NE SONT PAS REPRIS, faute d'être des faits de cette
 * page : `Localisation`, `Niveau d'ouverture`, `Calibre`. Rien n'est inventé
 * pour combler la place, et quatre faits que la carte n'a pas (plages horaires,
 * pas de la courbe, consentement, sens servis) restent propres à cette page.
 */
export function enteteDuCompteur(params: {
    prm?: string | null
    informations: InformationPDL | null | undefined
    directions: DirectionEnedis[]
}): FaitEntete[] {
    const info = params.informations
    const compteur = info?.compteur

    return [
        {
            cle: "prm",
            libelle: "Identifiant du point",
            valeur: texte(params.prm),
            mono: true,
        },
        {
            cle: "segment",
            libelle: "Segment",
            valeur: texte(compteur?.segment),
        },
        {
            cle: "modele",
            libelle: "Modèle",
            valeur: texte(compteur?.modele),
        },
        {
            cle: "tension",
            libelle: "Tension de livraison",
            valeur: texte(compteur?.tension_livraison),
        },
        {
            cle: "releve",
            libelle: "Relevé",
            valeur: modaliteDeReleve(info),
        },
        {
            cle: "plages",
            libelle: "Plages horaires",
            valeur: plagesHoraires(info),
        },
        {
            cle: "pas",
            libelle: "Pas de la courbe",
            valeur: pasDeLaCourbe(info, params.directions[0] ?? "soutirage"),
        },
        {
            cle: "raccordement",
            // ⚠️ LA RÈGLE DU LOT 5g PRIME SUR L'ALIGNEMENT (S2) : sur un
            // producteur pur, le mot « soutirage » ne doit apparaître NULLE
            // PART — il n'y a rien à soutirer sur ce point, et le lire donne à
            // croire à une consommation. Le libellé de la carte
            // (« Puiss. de racc. en soutirage ») portait ce mot et faisait
            // rougir le récit P4 ; là, et là seulement, la page garde son
            // libellé neutre. Partout ailleurs, c'est celui de la carte.
            libelle: params.directions.includes("soutirage")
                ? "Puiss. de racc. en soutirage"
                : "Puissance de raccordement",
            valeur: kva(
                compteur?.raccordement?.puissance_raccordement_soutirage
            ),
        },
        {
            cle: "calendrier",
            libelle: "Calendrier fournisseur",
            valeur: calendrierFournisseur(info),
        },
        {
            cle: "contrat",
            libelle: "FTA",
            valeur: formuleTarifaire(info),
        },
        {
            cle: "souscrite",
            libelle: "Puiss. souscrite",
            valeur: puissanceSouscrite(info),
        },
        {
            cle: "denivele",
            libelle: "Dénivelé de puissance",
            valeur: deniveleSouscrit(info),
        },
        {
            cle: "consentement",
            libelle: "Consentement",
            valeur: consentementServi(info),
        },
        {
            cle: "mise-en-service",
            libelle: "Mise en service",
            valeur: miseEnService(info, params.directions),
        },
        {
            cle: "production",
            libelle: "Installation de production",
            valeur: installationDeProduction(info),
        },
        {
            cle: "directions",
            libelle: "Directions",
            valeur: directionsServies(params.directions),
        },
    ]
}

/** Les faits réellement connus : l'écran ne peint que ceux-là. */
export function faitsConnus(faits: FaitEntete[]): FaitEntete[] {
    return faits.filter((fait) => fait.valeur !== null)
}
